import React, { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { libraryAPI } from '../lib/api';
import { VideoCard } from '../components/VideoCard';
import { ContinueWatching } from '../components/ContinueWatching';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Loader2, FolderOpen, ArrowRight } from 'lucide-react';

export const MyLibraryPage = () => {
    const { isAuthenticated, user, loading: authLoading } = useAuth();
    const [library, setLibrary] = useState({ workshops: [], courses: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchLibrary = async () => {
            if (!isAuthenticated) return;
            
            try {
                const response = await libraryAPI.getMyLibrary();
                setLibrary(response.data);
            } catch (err) {
                setError('Failed to load your library');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        if (isAuthenticated) {
            fetchLibrary();
        }
    }, [isAuthenticated]);

    // Wait for auth check
    if (authLoading) {
        return (
            <div className="min-h-[50vh] flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
        );
    }

    // Redirect if not authenticated
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    if (loading) {
        return (
            <div className="min-h-[50vh] flex items-center justify-center" data-testid="library-loading">
                <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
        );
    }

    const hasContent = library.workshops.length > 0 || library.courses.length > 0;

    return (
        <div className="section-gap" data-testid="my-library-page">
            <div className="container-main">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="font-serif text-4xl md:text-5xl font-semibold text-primary">
                        My Library
                    </h1>
                    <p className="text-muted-foreground mt-2">
                        Welcome back, {user?.email}
                    </p>
                </div>

                {error && (
                    <div className="text-center py-12" data-testid="library-error">
                        <p className="text-destructive">{error}</p>
                    </div>
                )}

                {!error && !hasContent && (
                    <div className="text-center py-16" data-testid="library-empty">
                        <div className="w-20 h-20 mx-auto rounded-full bg-secondary flex items-center justify-center mb-6">
                            <FolderOpen className="h-10 w-10 text-muted-foreground" />
                        </div>
                        <h2 className="font-serif text-2xl font-medium mb-2">
                            Your library is empty
                        </h2>
                        <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                            You don't have any purchased content yet. 
                            Browse our courses and workshops to start learning.
                        </p>
                        <div className="flex justify-center gap-4">
                            <Button asChild>
                                <Link to="/courses">
                                    Browse Courses
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </Link>
                            </Button>
                            <Button asChild variant="outline">
                                <Link to="/workshops">
                                    View Painting Videos
                                </Link>
                            </Button>
                        </div>
                    </div>
                )}

                {hasContent && (
                    <>
                        {/* Continue Watching Section */}
                        <ContinueWatching />
                        
                        <Tabs defaultValue="all" className="w-full" data-testid="library-tabs">
                        <TabsList className="mb-8">
                            <TabsTrigger value="all" data-testid="tab-all">
                                All ({library.workshops.length + library.courses.length})
                            </TabsTrigger>
                            <TabsTrigger value="courses" data-testid="tab-courses">
                                Courses ({library.courses.length})
                            </TabsTrigger>
                            <TabsTrigger value="workshops" data-testid="tab-workshops">
                                Painting Videos ({library.workshops.length})
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="all" className="mt-0">
                            {/* Courses Section */}
                            {library.courses.length > 0 && (
                                <div className="mb-12">
                                    <h2 className="font-serif text-2xl font-medium mb-6">Courses</h2>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                        {library.courses.map((course) => (
                                            <VideoCard 
                                                key={course.course_id} 
                                                video={{
                                                    id: course.course_id,
                                                    title: course.title,
                                                    instructor: course.instructor,
                                                    thumbnail: course.thumbnail,
                                                    lessons: course.lessons,
                                                    course_id: course.course_id,
                                                }} 
                                                type="course" 
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Workshops Section */}
                            {library.workshops.length > 0 && (
                                <div>
                                    <h2 className="font-serif text-2xl font-medium mb-6">Painting Videos</h2>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                        {library.workshops.map((workshop) => (
                                            <VideoCard 
                                                key={workshop.id} 
                                                video={workshop} 
                                                type="workshop" 
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </TabsContent>

                        <TabsContent value="courses" className="mt-0">
                            {library.courses.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                    {library.courses.map((course) => (
                                        <VideoCard 
                                            key={course.course_id} 
                                            video={{
                                                id: course.course_id,
                                                title: course.title,
                                                instructor: course.instructor,
                                                thumbnail: course.thumbnail,
                                                lessons: course.lessons,
                                                course_id: course.course_id,
                                            }} 
                                            type="course" 
                                        />
                                    ))}
                                </div>
                            ) : (
                                <p className="text-center text-muted-foreground py-8">
                                    No courses in your library yet.
                                </p>
                            )}
                        </TabsContent>

                        <TabsContent value="workshops" className="mt-0">
                            {library.workshops.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                    {library.workshops.map((workshop) => (
                                        <VideoCard 
                                            key={workshop.id} 
                                            video={workshop} 
                                            type="workshop" 
                                        />
                                    ))}
                                </div>
                            ) : (
                                <p className="text-center text-muted-foreground py-8">
                                    No painting videos in your library yet.
                                </p>
                            )}
                        </TabsContent>
                    </Tabs>
                    </>
                )}
            </div>
        </div>
    );
};

export default MyLibraryPage;
